import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import blogService from "../../services/blog";
import queryDefault from "../../utils/queryDefault";

export default function BlogCreate() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);

  // Validation schema
  const schema = yup.object().shape({
    title: yup
      .string()
      .min(2)
      .required("title is a required field and length > 10"),
    short_description: yup.string().max(255, "Short Description max 255 char"),
    banner: yup
      .mixed()
      .required("Banner image is required")
      .test("fileSize", "File size is too large", (value) => {
        return value && value[0].size <= 5 * 1024 * 1024; // Limit file size to 5MB
      }),
  });

  const form = useForm({
    defaultValues: {
      title: "",
      banner: null,
      short_description: "",
      full_description: "",
      username: user.username,
      tag: "",
      is_carousel: false,
    },
    resolver: yupResolver(schema),
  });

  const createBlog = useMutation(
    "create_blog",
    (data: FormData) => blogService.createBlog(data),
    {
      onSuccess: () => {
        navigate("/app/cms/blog");
        queryClient.invalidateQueries(["blog_list"]);
        notification.onOpen({
          message: "Blog was successfully created!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("username", values.username);
    formData.append("short_description", values.short_description);
    formData.append("full_description", values.full_description);
    formData.append("tag", values.tag);
    formData.append("is_carousel", values.is_carousel);

    if (values.banner && values.banner[0]) {
      formData.append("banner", values.banner[0]); // Append file to FormData
    }

    createBlog.mutate(formData);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Blog"
        breadcrumbs={[
          { label: "Blog Management", href: "/app/cms/blog" },
          { label: "Create Blog", href: "/app/cms/blog/create" },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="blog"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Title</FormLabel>
                <Controller
                  name="title"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Tag</FormLabel>
                <Controller
                  name="tag"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <>
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                      <FormHelperText>Comma Separated</FormHelperText>
                    </>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Short Description</FormLabel>
                <Controller
                  name="short_description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                      multiline
                      rows={5}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Full Description</FormLabel>
                <Controller
                  name="full_description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                      multiline
                      rows={5}
                    />
                  )}
                />
              </FormControl>

              <FormControl sx={{ mt: 1 }}>
                <FormControlLabel
                  label="Is Carousel"
                  control={
                    <Controller
                      name="is_carousel"
                      control={form.control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          checked={form.getValues().is_carousel}
                        />
                      )}
                    />
                  }
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Banner Image</FormLabel>
                <Controller
                  name="banner"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => field.onChange(e.target.files)}
                      ref={ref}
                      style={{ marginTop: 8 }}
                    />
                  )}
                />
                {form.formState.errors.banner && (
                  <p style={{ color: "red" }}>
                    {form.formState.errors.banner.message}
                  </p>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createBlog.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
